/**
 * OneTrust Testing Helpers
 *
 * Utility functions for testing OneTrust consent management.
 * These should only be used in development/testing environments.
 */

/**
 * Clear all OneTrust consent data from cookies and localStorage
 * This is useful for testing fresh consent flows
 */
export const clearOneTrustConsent = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  console.log('[OneTrust Test] Clearing OneTrust consent data...')

  // Clear OneTrust cookies
  const oneTrustCookies = [
    'OptanonConsent',
    'OptanonAlertBoxClosed',
    'eupubconsent-v2',
    'eupubconsent',
  ]

  // Clear analytics cookies (for testing consent enforcement)
  const analyticsCookies = [
    '_dd_s',
    '_dd',
    'ajs_anonymous_id',
    'ajs_user_id',
    'ajs_group_id',
    '_ga',
    '_gid',
    '_gat',
  ]

  // Combine all cookies to clear
  const allCookies = [...oneTrustCookies, ...analyticsCookies]

  allCookies.forEach(cookieName => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.localhost;`
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost;`
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.lvh.me;`
  })

  // Clear OneTrust localStorage
  const localStorageKeys = Object.keys(localStorage)
  localStorageKeys.forEach(key => {
    if (key.includes('onetrust') || key.includes('Optanon')) {
      localStorage.removeItem(key)
    }
  })

  // Clear window.OnetrustActiveGroups
  if (window.OnetrustActiveGroups) {
    delete window.OnetrustActiveGroups
  }

  console.log(
    '[OneTrust Test] Consent data cleared. Reload the page to see a fresh consent banner.'
  )
  console.log('[OneTrust Test] After reload, you can test opt-out scenarios.')
}

/**
 * Log current OneTrust consent status for debugging
 */
export const debugOneTrustConsent = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  console.group('🔍 OneTrust Consent Debug Info')

  console.log('OneTrust Loaded:', typeof window.OneTrust !== 'undefined')
  console.log('Active Groups:', window.OnetrustActiveGroups || 'Not set')

  // Check cookies (OneTrust + Analytics)
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [name, value] = cookie.trim().split('=')
    if (
      name.includes('Optanon') ||
      name.includes('onetrust') ||
      name.includes('_dd') ||
      name.includes('ajs_') ||
      name.includes('_ga')
    ) {
      acc[name] = value
    }
    return acc
  }, {} as Record<string, string>)

  console.log('OneTrust Cookies:', cookies)

  // Check localStorage
  const localStorageData: Record<string, string> = {}
  Object.keys(localStorage).forEach(key => {
    if (key.includes('onetrust') || key.includes('Optanon')) {
      localStorageData[key] = localStorage.getItem(key) || ''
    }
  })

  console.log('OneTrust localStorage:', localStorageData)

  // Decode OptanonConsent if present
  const optanonConsent = cookies['OptanonConsent']
  if (optanonConsent) {
    try {
      const decoded = decodeURIComponent(optanonConsent)
      console.log('Decoded OptanonConsent:', decoded)

      // Parse groups
      const groupsMatch = decoded.match(/groups=([^&]+)/)
      if (groupsMatch) {
        console.log('Consent Groups from Cookie:', groupsMatch[1])
      }
    } catch (e) {
      console.error('Failed to decode OptanonConsent:', e)
    }
  }

  console.groupEnd()
}

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  ;(window as any).__clearOneTrustConsent = clearOneTrustConsent
  ;(window as any).__debugOneTrustConsent = debugOneTrustConsent
}

export default {
  clearOneTrustConsent,
  debugOneTrustConsent,
}
